<template>
    <div class="accept-terms inner p-15px">
        <h1 class="accept-terms__title">ご利用にあたっての確認</h1>
        <div class="accept-terms__intro">
            <p>
                本サービスをご利用いただくには、利用規約および個人情報の取扱いへの同意が必要です。
            </p>
            <p>
                AIユイゴン&nbsp;Well-B等の連携サービスからログインされた方で、会員登録画面での同意手続きが未完了の場合は、以下よりご同意をお願いいたします。
            </p>
            <p>
                また、連携サービス経由のお客様は、当サービスのご利用に必要な情報の一部が未入力となっている場合があります。お手数をおかけしますが、下記の項目をご確認・ご入力のうえ、同意にお進みください。
            </p>
        </div>
        <el-form ref="acceptTermsForm" :rules="rules" :model="profile" class="form accept-terms__form">
            <!--
              プロフィール情報の確認・補完セクション: 連携サービス経由の会員に不足しがちな項目
              (gender/name/name_kana/birthday/prefecture/use_mail_magazine) を、GET
              /member/selfInfoから自動入力した上で編集可能にし、全項目必須で
              POST /member/accept-termsへ送信する。
            -->
            <div class="section">
                <h2 class="section__title">
                    <span class="section__index">1</span>
                    お客様情報の確認・入力
                </h2>
                <p class="section__description">
                    連携サービスの登録情報を反映しています。内容にお間違いがないかご確認のうえ、未入力の項目をご入力ください。
                </p>
                <table class="table-register accept-terms__profile-table">
                    <tr>
                        <th id="profile-name" class="column-label required">{{ $t('member.name') }}</th>
                        <td class="column-input">
                            <div class="d-flex">
                                <el-form-item-custom prop="lastName" class="w-45p mr-auto ">
                                    <input type="text" v-model="profile.lastName" placeholder="姓" class="input">
                                </el-form-item-custom>
                                <el-form-item-custom prop="firstName" class="w-45p ">
                                    <input type="text" v-model="profile.firstName" placeholder="名" class="input">
                                </el-form-item-custom>
                            </div>
                            <span class="d-inline-block small-text">連携サービスの登録情報をもとに自動入力されます。お間違いがある場合は修正してください</span>
                        </td>
                    </tr>
                    <tr>
                        <th id="profile-name-kana" class="column-label required">{{ $t('member.name_kana') }}</th>
                        <td class="column-input">
                            <div class="d-flex">
                                <el-form-item-custom prop="lastNameKana" class="w-45p mr-auto ">
                                    <input type="text" v-model="profile.lastNameKana" placeholder="セイ" class="input">
                                </el-form-item-custom>
                                <el-form-item-custom prop="firstNameKana" class="w-45p ">
                                    <input type="text" v-model="profile.firstNameKana" placeholder="メイ" class="input">
                                </el-form-item-custom>
                            </div>
                            <span class="d-inline-block small-text">連携サービスでは入力必須ではないため、未入力の場合はご入力ください</span>
                        </td>
                    </tr>
                    <tr>
                        <th id="profile-gender" class="column-label required">{{ $t('member.gender') }}</th>
                        <td class="column-input">
                            <el-form-item-custom prop="gender" class="form-item-radio">
                                <el-radio-group v-model="profile.gender">
                                    <el-radio :key="1" :label="'1'">{{ $t('commons.male') }}</el-radio>
                                    <el-radio :key="2" :label="'2'">{{ $t('commons.female') }}</el-radio>
                                </el-radio-group>
                            </el-form-item-custom>
                        </td>
                    </tr>
                    <tr>
                        <th id="profile-birthday" class="column-label required">{{ $t('member.birthday') }}</th>
                        <td id="birthday" class="column-input xxs-input-birthday">
                            <el-form-item-custom prop="birthday">
                                <date-select v-model="profile.birthday" :defaultYear="defaultBirthdayYear"></date-select>
                            </el-form-item-custom>
                            <span class="d-inline-block small-text important-note">パスワード再設定時の本人確認に使用される重要な項目です</span>
                        </td>
                    </tr>
                    <tr>
                        <th id="profile-prefecture" class="column-label required">{{ $t('member.prefecture') }}</th>
                        <td class="column-input max-width-220">
                            <el-form-item-custom prop="prefecture">
                                <prefecture-select
                                    v-model="profile.prefecture"
                                    :prefectureMaster="prefectureMaster"
                                ></prefecture-select>
                            </el-form-item-custom>
                        </td>
                    </tr>
                    <tr>
                        <th id="profile-use-mail-magazine" class="column-label required">{{ $t('member.use_mail_magazine') }}</th>
                        <td class="column-input max-width-220">
                            <el-radio-group v-model="profile.useMailMagazine">
                                <el-radio :key="true" :label="true">{{ $t('commons.yes') }}</el-radio>
                                <el-radio :key="false" :label="false">{{ $t('commons.no') }}</el-radio>
                            </el-radio-group>
                        </td>
                    </tr>
                </table>
            </div>

            <div class="section">
                <h2 class="section__title">
                    <span class="section__index">2</span>
                    利用規約・個人情報の取扱いへの同意
                </h2>
                <div class="checkbox-area">
                    <el-checkbox v-model="isAgreeMemberAgreement" :class="checkPolicyClass" @change="handlePolicy">
                        <a href="https://legacy.ne.jp/privacy-customer/" target="_blank" rel="noopener noreferrer">
                            <span class="active-link">{{ $t('words.member.agree_member_agreement') }}</span>
                        </a>
                    </el-checkbox>
                    <div class="warning-policy" v-if="checkPolicy">
                        「『個人情報の取扱いについて』同意します」にチェックしてください。
                    </div>
                    <el-checkbox v-model="isAgreeTermsOfService" :class="checkTermClass" @change="handleTerm">
                        <router-link to="/termsOfService" target="_blank">
                            <span class="active-link">{{ $t('words.member.agree_terms_of_service') }}</span>
                        </router-link>
                    </el-checkbox>
                    <div class="warning-term" v-if="checkTerm">
                        「利用規約に同意します」にチェックしてください。
                    </div>
                </div>
            </div>

            <div class="accept-terms__actions">
                <el-button type="primary" :disabled="disabled" @click="handleAccept">同意してサービスを利用する</el-button>
            </div>
        </el-form>
    </div>
</template>

<script>
import DateSelect from '@/components/common/DateSelect'
import PrefectureSelect from '@/components/common/PrefectureSelect'
import ContactApi from '@/api/contact/contact-cl'
import memberApi from '@/api/member/member-client.js'
import { applySessionFromResponse } from '@/common/authSession'
import moment from 'moment'

export default {
    name: 'AcceptTerms',
    components: {
        DateSelect,
        PrefectureSelect
    },
    data () {
        const validateDate = (rule, value, callback) => {
            if (!moment(value, 'YYYY-MM-DD', true).isValid()) {
                callback(new Error('正しい日付を選択してください'))
            } else {
                callback()
            }
        }

        return {
            isAgreeMemberAgreement: false,
            isAgreeTermsOfService: false,
            checkPolicy: false,
            checkTerm: false,
            disabled: false,
            profile: {
                lastName: '',
                firstName: '',
                lastNameKana: '',
                firstNameKana: '',
                gender: '',
                birthday: '',
                prefecture: '',
                useMailMagazine: true
            },
            rules: {
                lastName: [
                    { required: true, message: this.$t('rules.required', { key: this.$t('member.last_name') }), trigger: 'blur' },
                    { max: 50, message: '50文字以内で入力してください' }
                ],
                firstName: [
                    { required: true, message: this.$t('rules.required', { key: this.$t('member.first_name') }), trigger: 'blur' },
                    { max: 50, message: '50文字以内で入力してください' }
                ],
                lastNameKana: [
                    { pattern: /^[ァ-ンヴー]+$/, message: '全角カタカナで入力してください' },
                    { max: 50, message: '50文字以内で入力してください' },
                    { required: true, message: this.$t('rules.required', { key: this.$t('member.last_name_kana') }), trigger: 'blur' }
                ],
                firstNameKana: [
                    { pattern: /^[ァ-ンヴー]+$/, message: '全角カタカナで入力してください' },
                    { max: 50, message: '50文字以内で入力してください' },
                    { required: true, message: this.$t('rules.required', { key: this.$t('member.first_name_kana') }), trigger: 'blur' }
                ],
                gender: [
                    { required: true, message: this.$t('rules.no_select', { key: this.$t('member.gender') }), trigger: 'change' }
                ],
                birthday: [
                    { required: true, message: this.$t('rules.no_select', { key: this.$t('member.birthday') }), trigger: 'blur' },
                    { validator: validateDate, trigger: 'blur' }
                ],
                prefecture: [
                    { required: true, message: this.$t('rules.no_select', { key: this.$t('member.prefecture') }), trigger: 'blur' }
                ]
            },
            prefectureMaster: [],
            defaultBirthdayYear: '1970'
        }
    },
    computed: {
        checkPolicyClass () {
            return this.checkPolicy ? 'is-error' : ''
        },
        checkTermClass () {
            return this.checkTerm ? 'is-error' : ''
        }
    },
    async created () {
        await this.loadMemberInfo()
        await this.loadPrefecture()
    },
    methods: {
        // 連携サービス経由の会員は、既存のプロフィール項目 (UMSからの情報) をそのまま反映し、
        // 不足している項目のみ空欄のまま表示する - このままレビュー・修正して提出できる。
        async loadMemberInfo () {
            const member = await memberApi.getLoginUserInfo()
            this.profile.lastName = member.lastName || ''
            this.profile.firstName = member.firstName || ''
            this.profile.lastNameKana = member.lastNameKana || ''
            this.profile.firstNameKana = member.firstNameKana || ''
            this.profile.gender = (member.gender === '1' || member.gender === '2') ? member.gender : ''
            this.profile.birthday = member.birthday || ''
            this.profile.prefecture = member.prefecture || ''
            this.profile.useMailMagazine = typeof member.useMailMagazine === 'boolean' ? member.useMailMagazine : true
        },
        async loadPrefecture () {
            const res = await ContactApi.getInformPrefectureMaster()
            this.prefectureMaster = res.data.data
        },
        formatDate (date, format = 'YYYY-MM-DD', defaultValue = null) {
            return date !== null && date !== undefined && date !== '' ? moment(date).format(format) : defaultValue
        },
        handlePolicy () {
            this.checkPolicy = !this.isAgreeMemberAgreement
        },
        handleTerm () {
            this.checkTerm = !this.isAgreeTermsOfService
        },
        handleAccept () {
            this.checkPolicy = !this.isAgreeMemberAgreement
            this.checkTerm = !this.isAgreeTermsOfService
            if (!this.isAgreeMemberAgreement || !this.isAgreeTermsOfService) {
                return
            }
            this.$refs.acceptTermsForm.validate((valid) => {
                if (!valid) {
                    this.alertMessage('入力情報を再度ご確認ください。', '入力情報エラー')
                    return
                }
                this.disabled = true
                const requestData = {
                    lastName: this.profile.lastName,
                    firstName: this.profile.firstName,
                    lastNameKana: this.profile.lastNameKana,
                    firstNameKana: this.profile.firstNameKana,
                    gender: this.profile.gender,
                    birthday: this.formatDate(this.profile.birthday),
                    prefecture: this.profile.prefecture,
                    useMailMagazine: this.profile.useMailMagazine
                }
                memberApi.acceptTerms(requestData).then(response => {
                    return applySessionFromResponse(this.$store, response).then(() => {
                        this.$router.push({ name: 'portalMember' })
                    })
                }).catch(error => {
                    this.disabled = false
                    this.alertMessage(error.response.data.message, 'エラー')
                })
            })
        },
        alertMessage (message, title) {
            this.$alert(message, title, {
                confirmButtonText: this.$t('commons.ok')
            })
        }
    }
}
</script>

<style scoped>
    @import '../31_member/member_register.css';

    .accept-terms {
        padding-bottom: 3em;
    }

    .accept-terms__title {
        margin: 0 0 1em;
        text-align: center;
        font-size: 28px;
        font-weight: bold;
        color: #333;
    }

    .accept-terms__intro {
        background: #fffdf7;
        border: 1px solid #c0b283;
        padding: 1.25em 1.5em;
        margin-bottom: 2em;
        font-size: 15px;
        line-height: 1.8;
        color: #333;
    }

    .accept-terms__intro p {
        margin: 0 0 0.75em;
    }

    .accept-terms__intro p:last-child {
        margin-bottom: 0;
    }

    .accept-terms__form {
        width: 100%;
    }

    .section {
        margin-bottom: 2.5em;
    }

    .section__title {
        display: flex;
        align-items: center;
        gap: 0.6em;
        margin: 0 0 0.75em;
        font-size: 18px;
        font-weight: bold;
        color: #333;
        border-bottom: 2px solid #c0b283;
        padding-bottom: 0.5em;
    }

    .section__index {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 1.6em;
        height: 1.6em;
        border-radius: 50%;
        background: #a5272a;
        color: #fff;
        font-size: 14px;
        flex-shrink: 0;
    }

    .section__description {
        margin: 0 0 1.25em;
        font-size: 14px;
        color: #666;
        line-height: 1.7;
    }

    .accept-terms__profile-table .small-text {
        display: block;
        margin-top: 0.35em;
        font-size: 12px;
        color: #888;
    }

    .accept-terms__profile-table .small-text.important-note {
        color: #a5272a;
        font-weight: bold;
    }

    .checkbox-area {
        margin-bottom: 0.5em;
    }

    .checkbox-area .el-checkbox {
        display: block;
        margin-bottom: 0.75em;
        padding-left: 2px;
    }

    .checkbox-area .is-error {
        background-color: #F5CCCC;
        width: 100%;
        padding-bottom: 2px;
        margin-right: 0;
    }

    .warning-policy,
    .warning-term {
        color: #F56C6C;
        font-size: 14px;
        margin: 0 0 1em 1.5em;
    }

    .active-link {
        color: #3a8ee6;
        text-decoration: underline;
    }

    .accept-terms__actions {
        text-align: center;
    }

    .accept-terms__complete {
        text-align: center;
        padding: 2em 1em;
        background: #f0f9eb;
        border: 1px solid #c2e7b0;
        border-radius: 4px;
        font-size: 16px;
        line-height: 1.8;
    }

    .accept-terms__complete p {
        margin: 0;
    }

    .accept-terms__complete-note {
        margin-top: 0.75em !important;
        font-size: 13px;
        color: #666;
    }
</style>
